import {
  pickBy,
  uniqBy,
  isArray,
  isEmpty,
  find,
  get,
  last,
  size,
  some,
  filter,
  map,
  isString,
  isNumber,
} from 'lodash';
import { format, getUnixTime, addDays } from 'date-fns';
import qs from 'qs';
import { put, all, delay, select, takeLatest } from 'redux-saga/effects';

import { ROUTE } from '../../../consts/pages';
import {
  FETCH_CALLS,
  SELECT_CALL,
  SET_CALLS,
  SET_CLIENT,
  SET_CLIENT_ERROR,
  SET_FILTERS,
  SET_PAGE,
  SET_SELECTED_CALL,
  UPDATE_CALLS,
  UPDATE_CLIENT,
  UPDATE_CLIENT_IN_ALL_CALLS,
  UPDATE_NEW_CALL,
} from '../../actions/calls';
import {
  WS_UPDATE_CALL_MESSAGE_RECEIVED,
  WS_UPDATE_CALL_REASONS_MESSAGE_RECEIVED,
  WS_UPDATE_CLIENT_MESSAGE_RECEIVED,
} from '../../actions/updates';
import {
  VOX_CALL_INCOMING,
  VOX_CALL_OUTGOING,
  VOX_SET_PBX_ID,
} from '../../../consts/vox';

import { parseCall, parseCalls } from '../../serializers/call';
import { anonymousPerson, serializePerson } from '../../serializers/person';
import { serializeClient } from '../../serializers/client';
import fetchData from '../../../api/fetchData';
import { serializeOrders } from '../../serializers/orders';

import { fetchTicketsByClientId } from '../tickets';
import { serializeTickets } from '../../serializers/tickets';

import { safeApply, safeCall } from '../errors';

const PERSONS_API_URL = `${process.env.API_URL}/persons`;
const TICKETS_API_URL = `${process.env.API_URL}/tickets`;

function* addPbxIdToNewCall() {
  const { calls: callState, dialer: dialerState } = yield select(
    state => state
  );
  const { calls } = callState;
  if (isEmpty(calls)) {
    return;
  }

  const emulatedCall = find(calls, { isEmulated: true });
  if (!emulatedCall) {
    return;
  }

  const { pbxId } = dialerState;
  yield put({
    payload: {
      call: {
        ...emulatedCall,
        call: {
          ...emulatedCall.call,
          isAnswered: true,
          pbxId,
        },
      },
    },
    type: UPDATE_NEW_CALL,
  });
}

const generateQueryParamsByCallState = state => {
  const { calls: callState, general: generalState } = state;
  const { calls, filters } = callState;
  const { dates, size, type } = filters;
  const isSupervisor = get(generalState, 'currentManager.isSupervisor');

  const params = {
    from_id: get(last(calls), 'call.id'),
    size,
    current_manager: !isSupervisor,
  };

  if (dates) {
    const [startDate, endDate] = dates;
    params.date = format(new Date(startDate), 'dd.MM.yyyy');
    params.date_end = format(new Date(endDate), 'dd.MM.yyyy');
  }

  switch (type) {
    case 'unanswered':
      params.is_answered = false;
      params.is_incoming = true;
      break;

    case 'outgoing':
      params.is_incoming = false;
      break;

    case 'incoming':
      params.is_incoming = true;
      break;

    default:
      break;
  }

  return qs.stringify(params);
};

function* fetchCalls() {
  const state = yield select(s => s);
  const { currentManager } = state.general;
  const { calls: prevCalls } = state.calls;

  if (!currentManager) {
    return;
  }

  const { isSupervisor } = currentManager;
  const queryParams = generateQueryParamsByCallState(state);
  const callsData = yield safeCall(
    fetchData,
    `${process.env.API_URL}/calls?${queryParams}`
  );
  const parsedCalls = yield safeCall(parseCalls, callsData, isSupervisor);
  const total = get(callsData, 'meta.total') || null;

  const mergedCalls = uniqBy([...prevCalls, ...parsedCalls], 'call.id');
  const callIds = mergedCalls.reduce(
    (prev, { call }) => ({ ...prev, [call.id]: true }),
    {}
  );

  yield put({
    payload: { calls: mergedCalls, callIds, total },
    type: SET_CALLS,
  });
}

function* selectCall({ payload: { history, id, forceSelection = false } }) {
  const { router } = yield select(state => state);
  const pathname = get(router, 'location.pathname', null);
  const isTicketPage = pathname.includes(ROUTE.TICKETS);

  if (!forceSelection && isTicketPage) return;

  yield safeCall(fetchCallClient);
  if (history) {
    history.push(`${ROUTE.CALLS}/${id}`);
  } else {
    window.history.replaceState({}, null, `${ROUTE.CALLS}/${id}`);
  }
}

function* fetchCallClient() {
  const { calls: callsState, general: generalState } = yield select(
    state => state
  );
  const isSupervisor = get(generalState, 'currentManager.isSupervisor');
  const { selectedCallId } = callsState;
  let { selectedCall } = callsState;

  const apiUrlCall = `${process.env.API_URL}/calls/${selectedCallId}`;
  const unparsedCallData = yield safeCall(fetchData, apiUrlCall);
  const {
    call_reasons: unparsedReasons,
    call: unparsedCall,
    orders: unparsedOrders,
    persons: unparsedPersons,
  } = unparsedCallData;

  selectedCall = yield safeCall(parseCall, unparsedCall, {
    isSupervisor,
    unparsedOrders,
    unparsedPersons,
    unparsedReasons,
  });

  const personType = get(selectedCall, 'contact.type', null);
  const clientId = get(selectedCall, 'contact.id', null);

  let tickets = [];
  let ordersWithTickets = [];
  let serializedPerson = anonymousPerson;

  if (!!clientId) {
    const apiUrl = `${PERSONS_API_URL}/${clientId}?type=${personType}`;
    const { person, orders } = yield safeCall(fetchData, apiUrl);
    tickets = yield safeCall(fetchTicketsByClientId, clientId);
    const serializedOrders = yield safeCall(serializeOrders, orders);
    serializedPerson = yield safeCall(serializePerson, person);
    ordersWithTickets = serializedOrders;

    if (isArray(serializedOrders) && serializedOrders.length > 0) {
      const ordersIds = serializedOrders.map(order => order.id);
      const existFilterFields = pickBy(
        {
          order_ids: ordersIds,
        },
        v => v !== undefined
      );
      const queryParams = qs.stringify(existFilterFields, {
        arrayFormat: 'brackets',
      });
      const { tickets: ordersTickets } = yield safeCall(
        fetchData,
        `${TICKETS_API_URL}?${queryParams}`
      );
      const serializedTickets = serializeTickets({ tickets: ordersTickets });
      ordersWithTickets = serializedOrders.map(order => ({
        ...order,
        tickets: serializedTickets,
      }));
    }
  }

  yield put({
    payload: {
      call: selectedCall,
      contact: serializedPerson,
      orders: ordersWithTickets,
      relatives: tickets,
    },
    type: SET_SELECTED_CALL,
  });
}

function* handleClient({ person, errors }) {
  if (person) {
    const serializedPerson = yield safeCall(serializePerson, person);
    yield put({ payload: { client: serializedPerson }, type: SET_CLIENT });
  } else {
    yield put({
      payload: { errors },
      type: SET_CLIENT_ERROR,
    });
  }
}

export function* updateClient({ payload }) {
  const { data } = payload;
  const apiUrl = `${PERSONS_API_URL}/${data.id}`;

  const res = yield safeCall(fetchData, apiUrl, {
    method: 'PUT',
    body: JSON.stringify({ ...data, type: 'client' }),
    headers: { 'Content-Type': 'application/json' },
  });

  yield safeCall(handleClient, res);
}

function* selectCurrentCall() {
  const {
    dialer: dialerState,
    calls: callState,
    router,
  } = yield select(state => state);

  const pathname = get(router, 'location.pathname', null);
  const isTicketPage = pathname.includes(ROUTE.TICKETS);

  if (isTicketPage) return;

  yield delay(1500); // TODO: Rethink algo of fetch new call

  const { contact, operator } = dialerState;
  const { calls } = callState;
  const { number: contactNumber } = contact;
  const { number: operatorNumber } = operator;

  const existedCall = find(
    calls,
    ({ call, contact }) =>
      call.isActive && [operatorNumber, contactNumber].includes(contact.phone)
  );

  if (existedCall) {
    yield put({ payload: { id: existedCall.call.id }, type: SELECT_CALL });
  }
}

function* updateCallByWS({ payload }) {
  const state = yield select(data => data);
  const isNextPageLoading = get(state.calls, 'isNextPageLoading');
  const total = get(state.calls, 'total');

  // Не нужно добавлять обновления по звонкам во время загрузки истории звонков, иначе возникнут
  // коллизии.
  if (isNextPageLoading && total === 0) {
    return;
  }

  const unparsedData = get(payload, 'data');

  if (!unparsedData) {
    return;
  }

  const {
    call_reasons: unparsedReasons,
    call: unparsedCall,
    orders: unparsedOrders,
    persons: unparsedPersons,
  } = unparsedData;

  const { id: currentManagerId, isSupervisor } = get(
    state.general,
    'currentManager'
  );
  const managerId = get(unparsedCall, 'manager_id');

  // Менеджер видит только обновления своих звонков, а супервизор всех.
  if (!isSupervisor && +currentManagerId !== managerId) {
    return;
  }

  const parsedCall = yield safeCall(parseCall, unparsedCall, {
    isSupervisor,
    unparsedOrders,
    unparsedPersons,
    unparsedReasons,
  });

  const { calls: callsState } = state;
  const { filters } = callsState;
  if (filters) {
    // Необходимо учитывать также фильтры перед применением обновления к списку звонков.
    const { dates } = filters;
    if (size(dates)) {
      const fromDate = getUnixTime(new Date(dates[0]));
      const toDate = getUnixTime(addDays(new Date(dates[1]), 1));
      const startedAt = get(parsedCall, 'call.startedAt');
      const finishedAt = get(parsedCall, 'call.finishedAt');
      const isInRange = startedAt <= toDate && finishedAt >= fromDate;
      if (!isInRange) {
        return;
      }
    }

    const { type } = filters;
    const isActive = get(parsedCall, 'call.isActive');
    const isAnswered = get(parsedCall, 'call.isAnswered');
    const isIncoming = get(parsedCall, 'call.isIncoming');
    switch (type) {
      case 'unanswered': {
        if (isActive || isAnswered || !isIncoming) {
          return;
        }
        break;
      }

      case 'outgoing': {
        if (isIncoming) {
          return;
        }
        break;
      }

      case 'incoming': {
        if (!isIncoming) {
          return;
        }
        break;
      }

      default:
        break;
    }
  }

  const { calls, selectedCallId } = callsState;
  const { call: cll } = parsedCall;
  cll.isListen =
    isSupervisor &&
    cll.isAnswered &&
    some(calls, c => c.call.id === cll.id && c.call.isListen);

  const isSelectedCall = selectedCallId === cll.id;

  /* eslint-disable-next-line no-shadow  */
  const existedCall = find(calls, ({ call }) => call.id === cll.id);
  if (
    isSelectedCall &&
    existedCall &&
    existedCall.contact.id !== parsedCall.contact.id
  ) {
    yield safeCall(fetchCallClient);
  }

  yield put({ payload: { call: parsedCall }, type: UPDATE_CALLS });
}

function* updateCallReasonsByWS({ payload }) {
  const unparsedData = get(payload, 'data');
  if (!unparsedData) {
    return;
  }

  const callId = get(payload, 'call_id');
  if (!callId) {
    return;
  }
  const serializedCallId = String(callId);

  const { calls, callIds } = yield select(state => state.calls);

  if (!callIds[serializedCallId]) return;

  const callForChange = find(calls, c => c.call.id === serializedCallId);
  if (!callForChange) {
    return;
  }

  let reasons = [];
  if (get(payload, 'is_deleted')) {
    const callReasonsIds = get(unparsedData, 'call_reason_ids');
    if (!Array.isArray(callReasonsIds)) return;
    reasons = filter(
      callForChange.call.reasons,
      ({ reasonId }) => !callReasonsIds.includes(+reasonId)
    );
  } else {
    const callReasons = get(unparsedData, 'call_reasons');
    if (Array.isArray(callReasons)) {
      reasons = map(
        callReasons,
        ({
          id,
          kind,
          reason_id, // eslint-disable-line camelcase
          subkind,
        }) => ({
          description: isString(subkind) ? subkind : '',
          id: isNumber(id) ? String(id) : null,
          reasonId: isNumber(reason_id) ? String(reason_id) : null,
          type: isString(kind) ? kind : '',
        })
      );
    }
  }

  const changedCall = {
    ...callForChange,
    call: {
      ...callForChange.call,
      reasons: filter(reasons, ({ id }) => isString(id)),
    },
  };

  yield put({ payload: { call: changedCall }, type: UPDATE_CALLS });
}

function* updateClientByWS({ payload }) {
  try {
    const selectedCall = yield select(state => state.calls);
    const clientId = get(selectedCall, 'contact.id', null);

    const unparsedData = get(payload, 'data');

    if (!unparsedData) return;
    if (clientId !== unparsedData.id) return; // update only currently selected client

    const client = serializeClient(unparsedData);
    yield put({ payload: { client }, type: UPDATE_CLIENT_IN_ALL_CALLS });
  } catch (error) {
    yield put({ error, type: UPDATE_CLIENT_IN_ALL_CALLS });
  }
}

export default function* () {
  yield all([
    takeLatest(
      [VOX_CALL_INCOMING, VOX_CALL_OUTGOING],
      safeApply(selectCurrentCall)
    ),
    takeLatest(VOX_SET_PBX_ID, safeApply(addPbxIdToNewCall)),
    takeLatest(UPDATE_CLIENT, safeApply(updateClient)),
    takeLatest(WS_UPDATE_CALL_MESSAGE_RECEIVED, safeApply(updateCallByWS)),
    takeLatest(
      WS_UPDATE_CALL_REASONS_MESSAGE_RECEIVED,
      safeApply(updateCallReasonsByWS)
    ),
    takeLatest(WS_UPDATE_CLIENT_MESSAGE_RECEIVED, safeApply(updateClientByWS)),
    takeLatest([FETCH_CALLS, SET_FILTERS, SET_PAGE], safeApply(fetchCalls)),
    takeLatest(SELECT_CALL, safeApply(selectCall)),
  ]);
}
